/* eslint-disable ember/no-runloop */
import { fn } from '@ember/helper';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed, set } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import { next } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismComboSelectInput,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import type {
  Hotel,
  Insightpagesfavourite,
  Market,
} from 'frontend-utils/models';
import GeneralDropdownsDropdownYield from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsTriggersTriggerDropdownBasic from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-basic';
import GeneralFilterBarDropdownsTriggersTriggerFilterBarFavourites from 'lumen/components/general/filter-bar/dropdowns/triggers/trigger-filter-bar-favourites';
import GeneralFilterBarEditFavourites from 'lumen/components/general/filter-bar/edit-favourites';
import GeneralFilterBarFilterBarPill from 'lumen/components/general/filter-bar/filter-bar-pill';
import GeneralFilterBarFormsFormFilterBarSaveFavourites from 'lumen/components/general/filter-bar/forms/form-filter-bar-save-favourites';
import { RESIZE_EVENTS } from 'lumen/services/resize';
import type { FilterBarPageType } from 'lumen/utils/constants/general-constants';
import { FILTER_BAR_PAGETYPES } from 'lumen/utils/constants/general-constants';

const DROPDOWN_WIDTH = 90;
const FILTER_PILL_MARGIN = 4;
const FILTER_PILL_MAX_WIDTH = 151;
const OFFSET_UNINITIALIZED_BUTTONS = 90;

interface GeneralFilterBarFilterBarSignature {
  Args: {
    activeFilters: unknown[];
    favourites?: Insightpagesfavourite[];
    filterBarSegmentationClicked: (
      segmentationId: string,
      segmentationValueId: string,
    ) => void;
    hideFavourites?: boolean;
    hotel?: Hotel | null;
    isFavouritesDisabled?: boolean;
    isHovered?: boolean;
    isLoading?: boolean;
    market?: Market | null;
    onClearFilterSelection: unknown;
    pageType?: FilterBarPageType;
    segmentationFilters?: string;
    setFilterValues?: (data: object) => void;
    side?: 'right';
    stayDateFilter: unknown;
  };
  Element: HTMLDivElement;
}

export default class GeneralFilterBarFilterBar extends Component<GeneralFilterBarFilterBarSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare resize: Services['resize'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked filterBarElement = null;
  @tracked isFilterBarHovered = false;
  @tracked isShowingEditFavouritesModal = false;
  @tracked isShowingSaveFavouritesModal = false;
  @tracked maxFilterBarWidth = null;

  // eslint-disable-next-line @typescript-eslint/require-await
  handleWindowResizeTask = task(async (initialize = false) => {
    if (!this.filterBarElement) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const filterContent =
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.filterBarElement.querySelector('.filter-content');
    if (!filterContent) {
      return;
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.filterBarElement.classList.add('filter-bar-full-width');

    const newMaxFilterBarWidth =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      filterContent.offsetWidth - this.offset(initialize);
    // @ts-expect-error: Incorrect type
    this.maxFilterBarWidth = newMaxFilterBarWidth;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.filterBarElement.classList.remove('filter-bar-full-width');
  });

  @computed(
    'args.{segmentationFilters.[],favourites.[],stayDateFilter}',
    'isShowingSaveFavouritesModal',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentlySelectedFavourite() {
    // We need to check if the current selection of filters is already saved as a favourite. This determines which filter action buttons we show to the user.
    let currentSelectedFavourite = null;

    if (this.args.segmentationFilters && this.args.favourites?.length) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      currentSelectedFavourite = this.checkIfFilterSelectionMatchesFavourite(
        this.args.favourites,
        this.args.segmentationFilters,
        this.args.stayDateFilter,
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return currentSelectedFavourite;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentlySelectedFavouriteObject() {
    return this.filterFavourites.filter(
      // @ts-expect-error: Incorrect type
      (favourite) => favourite.id === this.currentlySelectedFavourite,
    )[0];
  }

  @computed('filterFavourites.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get favouritesAdditionalAction() {
    if ((this.filterFavourites ?? []).length !== 0) {
      return {
        label: this.intl.t('insight_pages.favourites.edit_favourites'),
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClick: () => {
          this.showEditFavouritesModal();
        },
      };
    }

    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filterBarDropdownText() {
    return this.intl.t('general.amount_more', {
      count: this.filterCalculations.amountOfDropdownFilters,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filterBarOffset() {
    if (
      this.args.pageType === FILTER_BAR_PAGETYPES.REVENUE_INSIGHT ||
      this.args.pageType === FILTER_BAR_PAGETYPES.PARITY_ANALYTICS
    ) {
      return 75;
    }
    return 0;
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.activeFilters.@each.values',
    'args.{isLoading,pageType}',
    'filterBarElement',
    'filterBarOffset',
    'maxFilterBarWidth',
    'pillsData',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filterCalculations() {
    const activeFilters = this.args.activeFilters
      .flatMap((obj) =>
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        obj.values.map((valueObj) => ({
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          key: obj.key,
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          translationKey: obj.translationKey,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          value: valueObj,
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          formattedValue: obj.formattedValue,
        })),
      )
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      .sort((filterA, filterB) => filterA.key?.localeCompare(filterB.key));

    // Early return if no element or width
    if (!this.filterBarElement || !this.maxFilterBarWidth) {
      return {
        visibleFilters: [],
        dropdownFilters: activeFilters,
        amountOfDropdownFilters: activeFilters.length,
      };
    }

    // Special pages show all filters without dropdown
    if (
      this.args.pageType === FILTER_BAR_PAGETYPES.OVERVIEW_DASHBOARD ||
      this.args.pageType === FILTER_BAR_PAGETYPES.MARKET_INSIGHT
    ) {
      return {
        visibleFilters: activeFilters,
        dropdownFilters: [],
        amountOfDropdownFilters: 0,
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    let { amountOfVisiblePills, totalVisiblePillsWidth } = this.pillsData;
    const { pillsWidths } = this.pillsData;
    const filterBarWidth = this.maxFilterBarWidth - this.filterBarOffset;

    // Remove filters if wider than available width
    while (
      totalVisiblePillsWidth > filterBarWidth &&
      amountOfVisiblePills > 0
    ) {
      totalVisiblePillsWidth -= pillsWidths.pop();
      amountOfVisiblePills -= 1;
    }

    // Calculate additional possible filters
    const extraPills = Math.floor(
      (filterBarWidth - totalVisiblePillsWidth) / FILTER_PILL_MAX_WIDTH,
    );
    let remainingWidth =
      filterBarWidth -
      totalVisiblePillsWidth -
      extraPills * FILTER_PILL_MAX_WIDTH;
    const maxVisibleFilters = amountOfVisiblePills + extraPills;

    let invisibleFilters = activeFilters.length - maxVisibleFilters;

    // Adjust for dropdown width
    if (invisibleFilters > 0) {
      remainingWidth -= DROPDOWN_WIDTH;
      if (remainingWidth < 0) {
        invisibleFilters += 1;
        remainingWidth += FILTER_PILL_MAX_WIDTH;
      }
    }

    // Handle single remaining filter case
    if (
      invisibleFilters === 1 &&
      remainingWidth + DROPDOWN_WIDTH > FILTER_PILL_MAX_WIDTH
    ) {
      invisibleFilters = 0;
    }

    const actualVisibleCount = activeFilters.length - invisibleFilters;
    return {
      visibleFilters: activeFilters.slice(0, actualVisibleCount),
      dropdownFilters: activeFilters.slice(actualVisibleCount),
      amountOfDropdownFilters: invisibleFilters,
    };
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.pageType',
    'args.visibleFilters.[]',
    'mapFilterToOption',
    'sortedFavourites',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filterFavourites() {
    // Since we now have multiple page types in markets, we need to filter out the correct ones first
    const filteredFavourites = this.sortedFavourites.filter(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (favourite) => favourite.pageType === this.args.pageType,
    );

    if (this.args.pageType === FILTER_BAR_PAGETYPES.REVENUE_INSIGHT) {
      // RI is a special case, these favourites can contain breakdowns that have been hidden
      // In that case you cannot select them
      return (
        filteredFavourites
          .filter((savedFavourite) => {
            // Prevent failure to parse empty JSON payload
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (savedFavourite.filters.segmentationFilters) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const filters = JSON.parse(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                savedFavourite.filters.segmentationFilters,
              );

              let doesFavouriteContainHiddenSegmentations = false;

              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              if (Object.keys(filters).length) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                Object.keys(filters).forEach((segmentationId) => {
                  doesFavouriteContainHiddenSegmentations =
                    doesFavouriteContainHiddenSegmentations ||
                    this.checkIfFilterIsHidden(segmentationId);
                });
              }

              if (doesFavouriteContainHiddenSegmentations) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                set(savedFavourite, 'showAsDisabled', true);
              } else {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                set(savedFavourite, 'showAsDisabled', false);
              }
              return true;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (savedFavourite.filters.stayDateFilter) {
              // Absolutely necessary extra check. If there are no segmentation filters, but there are stayDateFilters...
              // ... then we want to add that favourite. No need to check hidden segmentations here.
              return true;
            }

            return false;
          })
          // eslint-disable-next-line @typescript-eslint/unbound-method
          .map(this.mapFilterToOption)
      );
    }

    // eslint-disable-next-line @typescript-eslint/unbound-method
    return filteredFavourites.map(this.mapFilterToOption);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isHovered() {
    return this.args.isHovered || this.isFilterBarHovered;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pillsData() {
    const pills = [
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      ...this.filterBarElement.querySelectorAll('.active-filter-btn'),
    ];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const totalVisiblePillsWidth = pills.reduce(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      (sum, pill) => sum + pill.offsetWidth + FILTER_PILL_MARGIN,
      0,
    );
    return {
      amountOfVisiblePills: pills.length,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      totalVisiblePillsWidth,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      pillsWidths: pills.map((pill) => pill.offsetWidth + FILTER_PILL_MARGIN),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showFilterActions() {
    return (
      !this.args.hideFavourites &&
      !this.args.isFavouritesDisabled &&
      this.args.activeFilters.length &&
      this.isHovered
    );
  }

  @computed('args.favourites.@each.order')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedFavourites() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return [...this.args.favourites].sort(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (favouriteA, favouriteB) => favouriteA.order - favouriteB.order,
    );
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);
    this.resize.registerResizeEvent(
      RESIZE_EVENTS.FILTER_BAR,
      this.onWindowResize.bind(this),
    );
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.router.on('routeDidChange', this.onRouteChange);
  }

  willDestroy(): void {
    super.willDestroy();

    this.resize.unregisterResizeEvent(RESIZE_EVENTS.FILTER_BAR);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.router.off('routeDidChange', this.onRouteChange);
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  checkIfFilterIsHidden(segmentationId) {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const visibleFilters = this.args.visibleFilters ?? [];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const filtersToCheckForVisibility =
      // @ts-expect-error: Incorrect type
      this.args.filtersToCheckForVisibility ?? [];
    // Don't need to do anything if there is no fisegmentationIdlters, or no custom visibleFilters (empty array = show everything)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (segmentationId && visibleFilters.length) {
      return (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        visibleFilters.indexOf(segmentationId) === -1 &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        filtersToCheckForVisibility.indexOf(segmentationId) !== -1
      );
    }
    return false;
  }

  // Helper function that returns an id or null if a the current filter selection matches a favourite or not
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  checkIfFilterSelectionMatchesFavourite(
    // @ts-expect-error: Incorrect type
    favourites,
    // @ts-expect-error: Incorrect type
    segmentationFilters,
    // @ts-expect-error: Incorrect type
    stayDateFilter,
  ) {
    // @ts-expect-error: Incorrect type
    let matchingFilterId = null;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment
    const parsedSegmentationFilters = JSON.parse(segmentationFilters);
    // used for comparison of keys later
    const objKeysParsedSegmentationFilters = Object.keys(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      parsedSegmentationFilters,
    );

    // @ts-expect-error: Incorrect type
    const matchedFavourites = [];

    // Compare the stored segmentationFilters (and stayDateFilter in case of RI) of one of the favourites is equal to the current segmentationFilters
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    favourites.forEach((savedFavourite) => {
      if (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        savedFavourite.filters?.segmentationFilters &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        savedFavourite.filters.segmentationFilters.length > 0
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const parsedFavouriteFilters = JSON.parse(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          savedFavourite.filters?.segmentationFilters,
        );

        const objKeysParsedFavouriteFilters = Object.keys(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          parsedFavouriteFilters,
        );

        // If number of keys of both objects aren't the same, we don't even have to start comparing
        if (
          objKeysParsedSegmentationFilters.length ===
          objKeysParsedFavouriteFilters.length
        ) {
          // Using sort() and toString() we can easily string compare both arrays
          const strSortedparsedSegmentationFiltersKeys =
            objKeysParsedSegmentationFilters.sort().toString();
          const strSortedparsedFavouriteFilters = objKeysParsedFavouriteFilters
            .sort()
            .toString();

          if (
            strSortedparsedSegmentationFiltersKeys ===
            strSortedparsedFavouriteFilters
          ) {
            let areAllSegmentationValuesEqual = true;

            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            Object.keys(parsedSegmentationFilters).forEach((segmentation) => {
              let strSortedSegmentationFilterValues = null;
              let strSortedFavouriteFilterValues = null;

              // Segmentation property value can be an array in case of pages like RI, RIMP, PI
              // In case this is not the case, the value will be a regular string, so we don't do the sorting (e.g. MI page)
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (Array.isArray(parsedSegmentationFilters[segmentation])) {
                // Using sort() and toString() we can easily string compare both arrays
                strSortedSegmentationFilterValues =
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  parsedSegmentationFilters[segmentation].sort().toString();
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                strSortedFavouriteFilterValues =
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                  parsedFavouriteFilters[segmentation].sort().toString();
              } else {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                strSortedSegmentationFilterValues =
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  parsedSegmentationFilters[segmentation];
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                strSortedFavouriteFilterValues =
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  parsedFavouriteFilters[segmentation];
              }

              if (
                strSortedSegmentationFilterValues &&
                strSortedFavouriteFilterValues &&
                strSortedSegmentationFilterValues !==
                  strSortedFavouriteFilterValues
              ) {
                areAllSegmentationValuesEqual = false;
              }
            });

            if (areAllSegmentationValuesEqual) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              matchingFilterId = savedFavourite.id;
            }
          }
        }

        // For RI check if the stayDateFilter is also equal!
        if (this.args.pageType === FILTER_BAR_PAGETYPES.REVENUE_INSIGHT) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (stayDateFilter !== savedFavourite.filters?.stayDateFilter) {
            matchingFilterId = null;
          }
        }

        // @ts-expect-error: Incorrect type
        if (matchingFilterId !== null) {
          // @ts-expect-error: Incorrect type
          matchedFavourites.push(matchingFilterId);
        }
      }
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return matchedFavourites[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  offset(initialize = false) {
    let offset = 0;
    if (
      initialize &&
      this.args.pageType === FILTER_BAR_PAGETYPES.REVENUE_INSIGHT
    ) {
      offset = OFFSET_UNINITIALIZED_BUTTONS;
    }
    return offset;
  }

  @action
  clearFilterSelection(): void {
    this.isFilterBarHovered = false;
    const objEmptyFilter = {
      segmentationFilters: '',
    };

    if (this.args.pageType === FILTER_BAR_PAGETYPES.REVENUE_INSIGHT) {
      // @ts-expect-error: Incorrect type
      objEmptyFilter.stayDateFilter = '';
    }

    // @ts-expect-error: Incorrect type
    this.args.setFilterValues(objEmptyFilter);

    // An extra function can be passed to the FilterBar to clean up additional variables/QP
    if (this.args.onClearFilterSelection) {
      // @ts-expect-error: Incorrect type
      this.args.onClearFilterSelection();
    }
  }

  @action
  closeEditFavouritesModal(): void {
    this.isShowingEditFavouritesModal = false;
  }

  @action
  closeSaveFavouritesModal(): void {
    this.isShowingSaveFavouritesModal = false;
  }

  @action
  deleteFilterGroup(): void {
    if (this.currentlySelectedFavourite) {
      this.store
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        .findRecord('insightpagesfavourite', this.currentlySelectedFavourite, {
          backgroundReload: false,
        })
        .then(
          (record) => {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            record.destroyRecord();
          },
          (response) => {
            if (
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              response?.errors &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              response.errors[0] &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              response.errors[0].detail
            ) {
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              this.alerting.showErrorAlert({
                text: this.intl.t('insight_pages.favourites.errors.delete'),
              });
            }
          },
        )
        .catch((e) => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.alerting.showErrorAlert();
          this.errorTracking.captureException(e);
        });
    }
  }

  @action
  didUpdateLoading(): void {
    if (!this.args.isLoading) {
      next(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.handleWindowResizeTask.perform(false);
      });
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  initialize(filterBarElement): void {
    if (!filterBarElement) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.filterBarElement = filterBarElement;
    next(() => {
      this.onWindowResize(true);
    });
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  mapFilterToOption(favourite) {
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      label: favourite.name,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      disabled: favourite.showAsDisabled,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      disabledMessage: favourite.showAsDisabled
        ? this.intl.t('insight_pages.favourites.hidden_breakdown')
        : undefined,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      favourite,
    };
  }

  @action
  // @ts-expect-error: Incorrect type
  onFavouriteChanged({ favourite: selectedFavourite }): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (!selectedFavourite.showAsDisabled) {
      this.usageAnalytics.track('Saved Filter Reused', {
        eventTrigger: 'filter-bar',
      });

      // If the user picks a new favourite, we set the segmentationFilters (& stayDateFilter value for RI) to the stored values in the favourite
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      this.args.setFilterValues(selectedFavourite.filters);
    }
  }

  @action
  onRouteChange(): void {
    next(() => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.handleWindowResizeTask.perform(false);
    });
  }

  @action
  onWindowResize(initialize = false): void {
    // Cancel any pending resize tasks
    if (this.handleWindowResizeTask.isRunning) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.handleWindowResizeTask.cancelAll();
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.handleWindowResizeTask.perform(initialize);
  }

  @action
  // @ts-expect-error: Incorrect type
  segmentationFilterClicked(segmentationId, segmentationValueId): void {
    if (
      this.args.activeFilters?.length === 1 &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.activeFilters[0]?.values?.length === 1
    ) {
      this.isFilterBarHovered = false;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument
    this.args.filterBarSegmentationClicked(segmentationId, segmentationValueId);
  }

  @action
  showEditFavouritesModal(): void {
    this.isShowingEditFavouritesModal = true;
  }

  @action
  showSaveFavouritesModal(): void {
    this.isShowingSaveFavouritesModal = true;
  }

  <template>
    <div
      class="filter-bar-container
        {{if (eq @side 'right') 'filter-bar-container--right'}}"
      {{didInsert this.initialize}}
      {{didUpdate this.didUpdateLoading @isLoading}}
      ...attributes
    >
      <div class="filter-bar">
        {{#unless @hideFavourites}}
          <div
            class="filter-favourites-button"
            data-test-id="filter-favourites-dropdown"
          >
            <PrismComboSelectInput
              @disabled={{@isFavouritesDisabled}}
              @noMatchesMessage={{t "insight_pages.favourites.no_favourites"}}
              @onChange={{this.onFavouriteChanged}}
              @options={{this.filterFavourites}}
              @unsafe_additionalAction={{this.favouritesAdditionalAction}}
              @unsafe_triggerComponent={{GeneralFilterBarDropdownsTriggersTriggerFilterBarFavourites}}
              @value={{this.currentlySelectedFavouriteObject}}
              data-test-button="Filter favorites"
            />
          </div>
        {{/unless}}
        <div class="filter-content" role="tooltip">
          <div class="filter-values flex flex-row">
            {{#each this.filterCalculations.visibleFilters as |activeFilter|}}
              <GeneralFilterBarFilterBarPill
                @activeFilter={{activeFilter}}
                @filter={{activeFilter.value}}
                @isLoading={{@isLoading}}
                @segmentationFilterClicked={{this.segmentationFilterClicked}}
              />
            {{/each}}
            {{#if this.filterCalculations.dropdownFilters.length}}
              <div
                class="dropdown-pills-wrapper"
                data-test-id="filter-overflow-dropdown"
              >
                <GeneralDropdownsDropdownYield
                  {{! @glint-expect-error: Incorrect type }}
                  @onCancel={{this.onCancel}}
                  {{! @glint-expect-error: Incorrect type }}
                  @selected={{this.filterBarDropdownText}}
                  @triggerComponent={{GeneralDropdownsTriggersTriggerDropdownBasic}}
                >
                  <section class="dropdown-pills">
                    {{#each
                      this.filterCalculations.dropdownFilters
                      as |activeFilter|
                    }}
                      <GeneralFilterBarFilterBarPill
                        @activeFilter={{activeFilter}}
                        @filter={{activeFilter.value}}
                        @isLoading={{@isLoading}}
                        @segmentationFilterClicked={{this.segmentationFilterClicked}}
                      />
                    {{/each}}
                  </section>
                </GeneralDropdownsDropdownYield>
              </div>
            {{/if}}
          </div>
          {{#if this.showFilterActions}}
            <div class="filter-actions-container">
              <div class="filter-actions-buttons-wrapper gap-100">
                {{#unless @hideFavourites}}
                  {{#if this.currentlySelectedFavourite}}
                    <PrismButton
                      @label={{t "insight_pages.favourites.delete_group"}}
                      @onClick={{this.deleteFilterGroup}}
                      @size="small"
                      data-event-id="filter-bar-favourite-delete"
                      data-event-value={{this.currentlySelectedFavourite}}
                      data-test-id="filter-delete"
                    />
                  {{else}}
                    <PrismButton
                      @label={{t "general.save"}}
                      @onClick={{fn this.showSaveFavouritesModal true}}
                      @size="small"
                      data-event-id="filter-bar-favourite-save-modal-open"
                      data-test-id="filter-save"
                    />
                  {{/if}}
                {{/unless}}
                <PrismButton
                  @buttonType="secondary"
                  @label={{t "general.remove"}}
                  @onClick={{this.clearFilterSelection}}
                  @size="small"
                  data-event-id="filter-bar-favourite-clear"
                  data-test-button="Clear filter"
                />
              </div>
            </div>
          {{/if}}
        </div>
      </div>
      {{#if this.isShowingSaveFavouritesModal}}
        <PrismModal
          @onClose={{this.closeSaveFavouritesModal}}
          @title={{t "insight_pages.favourites.modal_title"}}
        >
          <GeneralFilterBarFormsFormFilterBarSaveFavourites
            @closeModal={{this.closeSaveFavouritesModal}}
            @hotel={{@hotel}}
            @market={{@market}}
            @pageType={{@pageType}}
            @segmentationFilters={{@segmentationFilters}}
            @stayDateFilter={{@stayDateFilter}}
            class="modal--padded"
          />
        </PrismModal>
      {{/if}}
      {{#if this.isShowingEditFavouritesModal}}
        {{!
  Modal should have the height of the content
  The content does not expand or change in a way that changes the height
  This would give too many complications with the width calculation and would result in a permanent scrollbar
  }}
        <PrismModal
          @onClose={{this.closeEditFavouritesModal}}
          @title={{t "insight_pages.favourites.edit_favourites"}}
        >
          <GeneralFilterBarEditFavourites
            @closeModal={{this.closeEditFavouritesModal}}
            @favourites={{this.sortedFavourites}}
          />
        </PrismModal>
      {{/if}}
    </div>
  </template>
}
